import React from 'react';
import PropTypes from 'prop-types';
import { Input } from 'antd';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import Constants from 'utils/Constants';
import ComboBoxHOC from 'components/core/ComboBoxHOC';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import ProcessUtils from 'utils/ProcessUtils';
import VariableRequiredOptionsForm from 'components/core/forms/VariableRequiredOptionsForm';
import RetrievedVariableTransformation from 'components/core/fields/transformation_parameters/RetrievedVariableTransformation';
import { colorLabelBlue } from 'styles/base.js';
import _ from 'lodash';

export default class EditRetrievedVariablesModal extends React.Component {
    static propTypes = {
        process: PropTypes.object,
        selectedItems: PropTypes.object,
        onOkClicked: PropTypes.func,
        onCancelClicked: PropTypes.func
    };

    static VALIDATED_UNITS = {};

    constructor(props) {
        super(props);

        this.coordSystemList = ProcessUtils.getCoordinateSystemOptionsForVariable(props.process);

        this.selectedVariables = Object.keys(this.props.selectedItems).map((variableName) => {
            return (<li key={variableName}>{variableName}</li>);
        });

        //if ALL variables selected have the same value for one of the fields, set that as the field's inital value:
        //each item can have an 'undefined' state (for first time in loop to know it hasn't been set yet),
        // or a value, or 'null' means the vars don't match so don't set an initial value in the form
        let prefix = undefined;
        let inputDataset = undefined;
        let variableRequired = undefined;
        let retrieveQcVariable = undefined;
        let qcVariableRequired = undefined;
        let dataType = undefined;
        let outputDatastreams = undefined;
        let coordinateSystem = undefined;
        let units = undefined;
        let variableDimensions = {};

        _.forEach(props.selectedItems, (ignoredValue, retrievedVariableName) => {
            const retrievedVariable = props.process.variable_retrieval.retrieved_variables[retrievedVariableName];
            const varsOutputDatastreams = ProcessUtils.getRetrievedVariableOutputDatastreams(props.process, retrievedVariableName);

            variableRequired = this.setOrEmptyValue(variableRequired, retrievedVariable.variable_required);
            retrieveQcVariable = this.setOrEmptyValue(retrieveQcVariable, retrievedVariable.retrieve_qc_variable);
            qcVariableRequired = this.setOrEmptyValue(qcVariableRequired, retrievedVariable.qc_variable_required);
            dataType = this.setOrNullValue(dataType, retrievedVariable.data_type);
            coordinateSystem = this.setOrEmptyValue(coordinateSystem, retrievedVariable.coordinate_system);
            units = this.setOrNullValue(units, retrievedVariable.units);
            outputDatastreams = this.setOrNullArrayValue(outputDatastreams, varsOutputDatastreams);

            retrievedVariable.dims.forEach(dimensionName => {
                if (!variableDimensions[dimensionName]) {
                    variableDimensions = Object.assign({}, variableDimensions, {
                        [dimensionName]: null
                    });
                }
            });
            inputDataset = this.setOrNullValue(inputDataset, retrievedVariable.input_dataset);

            const underscore_pos = retrievedVariableName.indexOf('_');
            if (underscore_pos >= 0) {
                const var_prefix = retrievedVariableName.substring(0, underscore_pos);
                prefix = this.setOrNullValue(prefix, var_prefix);
            }
        });

        if (coordinateSystem === '') {
            coordinateSystem = undefined;
        }

        let isOverrideTableVisible = this.isTransformationOverrideVisible(props);
        if (coordinateSystem === null) {
            isOverrideTableVisible = null;
        }
        const overrideValues = this.getInitialOverrideValues(props, coordinateSystem);

        this.state = {
            invalid: null,
            variable_required: variableRequired,
            retrieve_qc_variable: retrieveQcVariable,
            qc_variable_required: qcVariableRequired,
            data_type: dataType,
            originalDataType: dataType,
            output_datastreams: outputDatastreams,
            coordinate_system: coordinateSystem,
            inputDataset,
            originalInputDataset: inputDataset,
            prefix,
            originalPrefix: prefix,
            units,
            originalUnits: units,
            isOverrideTableVisible,
            overrideValues,
            variableDimensions
        };
    }

    isTransformationOverrideVisible(props) {
        let displayOverride = false;
        Object.keys(props.selectedItems).forEach(variableName => {
            const variableHasOverride = ProcessUtils.variableHasTransformationOverride(props.process, 'transformation_type', variableName);

            displayOverride = variableHasOverride ? true : displayOverride;
        });

        return displayOverride;
    }

    getInitialOverrideValues(props, coordinateSystem) {
        let data = {};
        Object.keys(props.selectedItems).forEach(variableName => {
            const retrievedVariable = props.process.variable_retrieval.retrieved_variables[variableName];

            let coordinateSystemName = coordinateSystem;
            if (coordinateSystem === undefined) {
                // If we are not setting a new global coordinate system, then use the original coordinate system
                // from the retrieved variable
                coordinateSystemName = retrievedVariable.coordinate_system;
            }

            if (coordinateSystemName) {
                data = Object.assign({ [coordinateSystemName]: {} }, data);

                if (retrievedVariable.dims) {
                    retrievedVariable.dims.forEach(dimensionName => {
                        const variableOverrideValue = ProcessUtils.getVariableTransformationTypeOverride(props.process, variableName, dimensionName);
                        let overrideValue = data[coordinateSystemName][dimensionName];

                        if (typeof overrideValue === 'undefined') {
                            overrideValue = variableOverrideValue;
                        } else if (variableOverrideValue !== overrideValue) {
                            overrideValue = null;
                        }

                        data[coordinateSystemName] = Object.assign(data[coordinateSystemName], { [dimensionName]: overrideValue });
                    });
                }
            }
        });

        return data;
    }

    setOrNullValue = (variableToSet, valueToTest) => {
        if (variableToSet === undefined) {
            return valueToTest;
        } else if (variableToSet !== null && variableToSet !== valueToTest) {
            return null;
        }
        return variableToSet;
    };

    setOrEmptyValue = (variableToSet, valueToTest) => {
        // needed for coordinate system because 'null' is actually a selected value option,
        // instead use undefined to differentiate between no commonly selected value vs no transformation
        if (variableToSet === undefined) {
            return valueToTest;
        } else if (variableToSet !== '' && variableToSet !== valueToTest) {
            return '';
        }
        return variableToSet;
    };

    setOrNullArrayValue = (arrayToSet, arrayToTest) => {
        let ret = arrayToSet;
        if (arrayToSet === undefined) {
            ret = arrayToTest;

        } else {
            const isSame = _.isEmpty(_.difference(arrayToSet, arrayToTest)) && _.isEmpty(_.difference(arrayToTest, arrayToSet));
            if (!isSame) {
                ret = null;
            }
        }
        return ret;
    };
    
    getNewValues() {
        return this.state;
    }

    getInputDatasetOptions = () => {
        let inputDatasets = Object.keys(this.props.process.variable_retrieval.input_datasets);

        // If the current input dataset name (which user typed in to create new) is not in the
        // default list, then add it.
        if ( !_.includes(inputDatasets, this.state.inputDataset)) {
            inputDatasets = _.concat(this.state.inputDataset, inputDatasets);
        }
        return Constants.convertArrayToSelectOptions(inputDatasets);
    }

    setInvalid = (errorMessage) => {
        this.setState({ invalid: true, errorMessage });
    };

    onVariableRequiredChanged = (value) => {
        this.setState({ variable_required: value });
    };

    onRetrieveQCVariableChanged = (value) => {
        this.setState({ retrieve_qc_variable: value });
    };

    onQCVariableRequiredChanged = (value) => {
        this.setState({ qc_variable_required: value });
    };

    onPrefixChanged = (event) => {
        let prefix = event.target.value;
        if (!prefix) {
            prefix = null; // convert empty string to null
        }
        this.setState({ prefix });
    }

    onDataTypeChange = (newValue) => {
        const value = newValue ?  newValue.value : null;
        this.setState({ data_type: value });
    };

    onUnitsChange = (newValue) => {
        const value = newValue ?  newValue.value : null;
        this.setState({ units: value });
    };

    onInputDatasetChange = (event) => {
        let value = null;
        if (event !== null) {
            value = event.value;
        }
        this.setState({
            inputDataset: value
        });
    };

    onOverrideValueChange = (fieldPath, value, onChangeData) => {
        const coordinateSystemName = onChangeData.coordinateSystemName;
        const dimensionName = onChangeData.dimensionName;

        const overrideValues = _.cloneDeep(this.state.overrideValues);
        overrideValues[coordinateSystemName][dimensionName] = value.value;
        this.setState({ overrideValues });
    };

    onOverrideDisplayChange = (fieldPath, value) => {
        this.setState({ isOverrideTableVisible: value });
    };

    onOutputDatastreamChange = (newValue) => {
        // TODO: if the user has selected a datastream that is not yet an output datastream,
        // we need to prompt the user if they want to use the same variable names for the output datastream.  If they
        // say no, then when the dialog closes, we need to navigate to the output datastream page so they can set the
        // mappings.
        const output_datastreams = newValue.map(valueObj => {
            return valueObj.value;
        });
        this.setState({ output_datastreams });
    };

    onCoordinateSystemChange = (newValue) => {
        let coordinateSystemName = newValue.value;
        if (coordinateSystemName === '') {
            coordinateSystemName = null;
        }

        let isOverrideTableVisible = this.isTransformationOverrideVisible(this.props);
        if (coordinateSystemName === null) {
            isOverrideTableVisible = null;
        }
        const overrideValues = this.getInitialOverrideValues(this.props, coordinateSystemName);
        this.setState({ coordinate_system: coordinateSystemName, isOverrideTableVisible, overrideValues });
    };

    onDimensionChange = (oldDimensionName, e) => {
        const newDimensionsState = _.clone(this.state.variableDimensions);
        if (e !== null) {
            newDimensionsState[oldDimensionName] = e.value;
        } else {
            newDimensionsState[oldDimensionName] = null;
        }

        this.setState({ variableDimensions: newDimensionsState });
    };

    renderVariableDimensionsTable = () => {
        let dimensionOptions = [];
        if (this.state.coordinate_system) {
            dimensionOptions = ProcessUtils.getDimensionNamesAsArray(this.props.process, this.state.coordinate_system);
        }
        dimensionOptions = Constants.convertArrayToSelectOptions(dimensionOptions);

        const variableInputs = [];
        Object.keys(this.state.variableDimensions).forEach(dimensionName => {
            const value = Constants.convertValueToSelectValue(this.state.variableDimensions[dimensionName]);
            variableInputs.push(
                <tr key={`variable-dim-${dimensionName}`}>
                    <td className={css`vertical-align: middle !important;`}>{dimensionName}</td>
                    <td>
                        <ComboBoxHOC
                            creatable
                            classNamePrefix="react-select"
                            value={value}
                            options={dimensionOptions}
                            onChange={(e => { this.onDimensionChange(dimensionName, e)})}
                            isClearable
                        />
                    </td>
                </tr>
            );
        });

        return (
            <table className={`table table-bordered ${css`width:100%;`}`}>
                <tbody>
                {variableInputs}
                </tbody>
            </table>
        );
    };

    render() {
        const errorMessageRefFunction = (component) => {this.errorMessageComponent = component;};
        const className = this.state.invalid ? 'invalid' : '';
        const labelWidth = '75px';

        let selectedUnit = '';
        if (this.state.units !== null) {
            selectedUnit = { value: ProcessUtils.getUnits(this.state.units), label: ProcessUtils.getUnits(this.state.units) };
        }

        let outputDatastreams = [];
        if (this.state.output_datastreams) {
            outputDatastreams = Constants.convertArrayToSelectOptions(this.state.output_datastreams);
        }

        const coordinateSystemValue = ProcessUtils.getCoordinateSystemSelectValue(this.state.coordinate_system, this.coordSystemList);
        const dataTypeValue = this.state.data_type ? Constants.convertValueToSelectValue(this.state.data_type) : null;
        const listStyle = css`
            li {
                color: ${colorLabelBlue};
                list-style-type: none;
                font-size: 12px;
            }
        `;

        let prefix = '';
        if (this.state.prefix !== null) {
            prefix = this.state.prefix;
        }

        const inputDatasetValue = this.state.inputDataset ? Constants.convertValueToSelectValue(this.state.inputDataset) : null;
        const canClearInputDataset = this.state.originalInputDataset ? false : true;
        const canClearUnits = this.state.originalUnits ? false : true;
        const canClearDataType = this.state.originalDataType ? false : true;

        return (
            <Flexbox flexDirection="column">
                <Flexbox flexDirection="row" className="scrollable multi-edit-variables">
                    <Flexbox flexGrow={1} flexDirection="column" justifyContent="flex-start" className="column rie-scrollable" style={{ paddingRight: '15px' }}>
                        <div className="section-title">Variables:</div>
                        <div className="section-body">
                            <ul className={listStyle}>
                                {this.selectedVariables}
                            </ul>
                        </div>
                    </Flexbox>
                    <Flexbox flexGrow={1} flexDirection="column" justifyContent="flex-start">
                        <div className="section-body">
                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Name Prefix" width={labelWidth}/>
                                <Input
                                    placeholder="Add prefix to variable names"
                                    defaultValue={prefix}
                                    onChange={this.onPrefixChanged}
                                    allowClear
                                />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Data Type" width={labelWidth}/>
                                <ComboBoxHOC
                                    classNamePrefix="react-select"
                                    value={dataTypeValue}
                                    options={Constants.DATA_TYPES}
                                    onChange={this.onDataTypeChange}
                                    isClearable={canClearDataType}
                                    inputId="data-type"
                                />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Units" width={labelWidth}/>
                                <ComboBoxHOC
                                    creatable
                                    classNamePrefix="react-select"
                                    value={selectedUnit}
                                    options={Constants.RETRIEVED_VARIABLE_UNITS}
                                    onChange={this.onUnitsChange}
                                    isClearable={canClearUnits}
                                    resetValue={''}
                                    placeholder="Select..."
                                    inputId="units"
                                />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Retrieval Rule Set" width={labelWidth}/>
                                <ComboBoxHOC
                                    creatable
                                    classNamePrefix="react-select"
                                    value={inputDatasetValue}
                                    options={this.getInputDatasetOptions()}
                                    onChange={this.onInputDatasetChange}
                                    isClearable={canClearInputDataset}
                                    name="inputDataset"
                                    inputId="input-dataset"
                                />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Output Datastream" width={labelWidth}/>
                                <ComboBoxHOC
                                    creatable
                                    classNamePrefix="react-select"
                                    value={outputDatastreams}
                                    options={ProcessUtils.getOutputDatstreamOptionsForVariable(this.props.process)}
                                    onChange={this.onOutputDatastreamChange}
                                    isMulti
                                    isClearable={false}
                                    inputId="output-datastream"
                                />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Coordinate System" width={labelWidth}/>
                                <ComboBoxHOC
                                    classNamePrefix="react-select"
                                    value={coordinateSystemValue}
                                    options={this.coordSystemList}
                                    onChange={this.onCoordinateSystemChange}
                                    inputId="coordinate-system"
                                />
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <InfoBoxLabel label="Variable Dimensions" width={labelWidth}/>
                                {this.renderVariableDimensionsTable()}
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <RetrievedVariableTransformation
                                    process={this.props.process}
                                    retrievedVariableNames={Object.keys(this.props.selectedItems)}
                                    isOverrideTableVisible={this.state.isOverrideTableVisible}
                                    onOverrideChange={this.onOverrideValueChange}
                                    onOverrideDisplayChange={this.onOverrideDisplayChange}
                                    overrideTableData={this.state.overrideValues}
                                    coordinateSystemName={this.state.coordinate_system}
                                />
                            </div>

                            <div className="colored-panel" style={{ marginBottom: '10px' }}>
                                <VariableRequiredOptionsForm
                                    process={this.props.process}
                                    variableRequired={this.state.variable_required}
                                    retrieveQCVariable={this.state.retrieve_qc_variable}
                                    qcVariableRequired={this.state.qc_variable_required}
                                    onVariableRequiredChanged={this.onVariableRequiredChanged}
                                    onRetrieveQCVariableChanged={this.onRetrieveQCVariableChanged}
                                    onQCVariableRequiredChanged={this.onQCVariableRequiredChanged}
                                    labelWrap
                                />
                            </div>

                        </div>
                    </Flexbox>
                </Flexbox>
                <div className={className} ref={errorMessageRefFunction}>
                    {this.state.errorMessage}
                </div>
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/EditRetrievedVariablesModal.js